/**
 * Runtime shape checks for anomaly REST responses. Each assert* function
 * throws a descriptive Error if the payload does not match the typed
 * contract from `./types`, otherwise narrows `unknown` to the typed shape.
 *
 * Hand-written (no Zod dep) because the surface area is small and these
 * validators are also a precise spec of what the backend must return.
 */
import {
  LoadResponse,
  StartResponse,
  ThresholdResponse,
  UseProcessedResponse,
  ValidateParamResponse,
  PlotData,
} from './types';

class ShapeError extends Error {
  constructor(endpoint: string, detail: string) {
    super(`Malformed ${endpoint} response: ${detail}`);
    this.name = 'ShapeError';
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isPlot(v: unknown): v is PlotData {
  if (!isObject(v)) return false;
  if (v.type !== 'line' && v.type !== 'scatter' && v.type !== 'multi') return false;
  if (!Array.isArray(v.traces)) return false;
  if (!isObject(v.xaxis) || typeof v.xaxis.label !== 'string') return false;
  if (v.xaxis.type !== 'datetime' && v.xaxis.type !== 'numeric') return false;
  if (!isObject(v.yaxis) || typeof v.yaxis.label !== 'string') return false;
  if (typeof v.title !== 'string') return false;
  return true;
}

export function assertValidateParamResponse(
  v: unknown,
): asserts v is ValidateParamResponse {
  if (!isObject(v)) throw new ShapeError('/validate-param', 'not an object');
  if (typeof v.ok !== 'boolean') throw new ShapeError('/validate-param', 'ok must be boolean');
  if (v.error !== undefined && typeof v.error !== 'string') {
    throw new ShapeError('/validate-param', 'error must be string when present');
  }
}

export function assertLoadResponse(v: unknown): asserts v is LoadResponse {
  if (!isObject(v)) throw new ShapeError('/load', 'not an object');
  if (v.status !== 'loaded') throw new ShapeError('/load', 'status must be "loaded"');
  if (typeof v.columnName !== 'string') throw new ShapeError('/load', 'columnName must be string');
  if (typeof v.dtAvgH !== 'number') throw new ShapeError('/load', 'dtAvgH must be number');
  // timestepMin/offsetMin are an additive, non-critical contract (the read-only
  // info line). Tolerate their absence so the frontend keeps working against a
  // backend that doesn't return them yet; only reject a present-but-wrong type.
  if (v.timestepMin !== undefined && typeof v.timestepMin !== 'number') throw new ShapeError('/load', 'timestepMin must be number');
  if (v.offsetMin !== undefined && typeof v.offsetMin !== 'number') throw new ShapeError('/load', 'offsetMin must be number');
  if (typeof v.rowCount !== 'number') throw new ShapeError('/load', 'rowCount must be number');
  if (!isObject(v.plots)) throw new ShapeError('/load', 'plots missing');
  if (!isPlot(v.plots.original)) throw new ShapeError('/load', 'plots.original malformed');
  if (!isPlot(v.plots.slope)) throw new ShapeError('/load', 'plots.slope malformed');
}

function assertPartialPlots(endpoint: string, plots: unknown): void {
  if (!isObject(plots)) throw new ShapeError(endpoint, 'plots missing');
  for (const [k, val] of Object.entries(plots)) {
    if (val === undefined || val === null) continue;
    if (k === 'stlDecomposition') {
      if (!Array.isArray(val) || !val.every(isPlot)) {
        throw new ShapeError(endpoint, 'plots.stlDecomposition must be PlotData[]');
      }
      continue;
    }
    if (!isPlot(val)) throw new ShapeError(endpoint, `plots.${k} malformed`);
  }
}

export function assertStartResponse(v: unknown): asserts v is StartResponse {
  if (!isObject(v)) throw new ShapeError('/start', 'not an object');
  if (
    v.status !== 'awaiting_stl_threshold' &&
    v.status !== 'awaiting_lstm_threshold' &&
    v.status !== 'complete'
  ) {
    throw new ShapeError('/start', `unknown status: ${String(v.status)}`);
  }
  if (typeof v.sessionId !== 'string') throw new ShapeError('/start', 'sessionId must be string');
  assertPartialPlots('/start', v.plots);
  if (
    v.processedCsvFilename !== undefined &&
    v.processedCsvFilename !== null &&
    typeof v.processedCsvFilename !== 'string'
  ) {
    throw new ShapeError('/start', 'processedCsvFilename must be string|null when present');
  }
  // stlCoverage is an additive, non-critical contract. Tolerate absence; only
  // reject a present-but-wrong shape.
  if (v.stlCoverage !== undefined && v.stlCoverage !== null) {
    const c = v.stlCoverage;
    if (!isObject(c)) throw new ShapeError('/start', 'stlCoverage must be object when present');
    if (c.analyzedSegments !== null && typeof c.analyzedSegments !== 'number') {
      throw new ShapeError('/start', 'stlCoverage.analyzedSegments must be number|null');
    }
    if (c.skippedSegments !== null && typeof c.skippedSegments !== 'number') {
      throw new ShapeError('/start', 'stlCoverage.skippedSegments must be number|null');
    }
    if (c.warning !== undefined && typeof c.warning !== 'string') {
      throw new ShapeError('/start', 'stlCoverage.warning must be string when present');
    }
  }
}

export function assertThresholdResponse(
  v: unknown,
  endpoint: '/stl-threshold' | '/lstm-threshold',
): asserts v is ThresholdResponse {
  if (!isObject(v)) throw new ShapeError(endpoint, 'not an object');
  if (v.status !== 'awaiting_lstm_threshold' && v.status !== 'complete') {
    throw new ShapeError(endpoint, `unknown status: ${String(v.status)}`);
  }
  if (typeof v.sessionId !== 'string') throw new ShapeError(endpoint, 'sessionId must be string');
  assertPartialPlots(endpoint, v.plots);
  if (
    v.processedCsvFilename !== undefined &&
    v.processedCsvFilename !== null &&
    typeof v.processedCsvFilename !== 'string'
  ) {
    throw new ShapeError(endpoint, 'processedCsvFilename must be string|null when present');
  }
}

export function assertUseProcessedResponse(
  v: unknown,
): asserts v is UseProcessedResponse {
  if (!isObject(v)) throw new ShapeError('/use-processed', 'not an object');
  if (v.status !== 'loaded') throw new ShapeError('/use-processed', 'status must be "loaded"');
  if (typeof v.rowCount !== 'number') throw new ShapeError('/use-processed', 'rowCount must be number');
  if (typeof v.sessionId !== 'string') throw new ShapeError('/use-processed', 'sessionId must be string');
  if (!isObject(v.plots)) throw new ShapeError('/use-processed', 'plots missing');
  if (!isPlot(v.plots.original)) throw new ShapeError('/use-processed', 'plots.original malformed');
  if (!isPlot(v.plots.slope)) throw new ShapeError('/use-processed', 'plots.slope malformed');
}
